import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Grid,
  Button,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import axios from 'axios';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const DETAIL_ENDPOINT = ep('get_turquoise_torihikisaki_detail');

const FAR_FUTURE_END_DATE = '2999-12-31';
// ターコイズの日付未設定値 (税区分適用日など)
const EPOCH_DATE = '1900-01-01';

const TORIHIKISAKI_KBN_MAP: Record<string, { label: string; color: 'primary' | 'warning' | 'success' }> = {
  '1': { label: '得意先', color: 'primary' },
  '2': { label: '仕入先', color: 'warning' },
  '3': { label: '両方先', color: 'success' },
};

const HOJIN_KBN_MAP: Record<string, string> = {
  '1': '法人',
  '2': '個人',
  '3': '官公庁',
  '4': 'その他',
};

// ---- 区分値の名称マップ (MS_TORIHIKISAKI のカラムコメント定義に準拠) ----
// 入金区分 / 支払区分
const NYUKIN_KBN_MAP: Record<string, string> = {
  '1': '現金', '2': '小切手', '3': '手形', '4': '振込', '5': '値引', '6': '相殺', '7': '手数料', '9': 'その他',
};
// 集金方法 / 支払方法
const HOHO_KBN_MAP: Record<string, string> = { '1': '訪問', '2': '郵送', '3': '振込', '9': 'なし' };
// 預金種別 (VE_FURIKOMI_KOZA のビュー定義に準拠)
const YOKINSHU_MAP: Record<string, string> = { '1': '普通預金', '2': '当座預金' };
// 振込手数料負担区分 (VE_FURIKOMI_KOZA のビュー定義に準拠)
const TESURYO_FUTAN_KBN_MAP: Record<string, string> = { '1': '自社負担', '2': '相手負担' };
// 税区分 (旧/新)
const ZEI_KBN_MAP: Record<string, string> = { '1': '外税', '2': '内税', '3': '免税', '4': '非課税', '5': '無処理' };
// 税計算区分
const ZEI_KEISAN_KBN_MAP: Record<string, string> = { '1': '明細単位', '2': '伝票単位', '3': '締単位' };
// 税端数区分 / 金額端数区分
const HASU_KBN_MAP: Record<string, string> = { '1': '切捨', '2': '四捨五入', '3': '切上' };
// 請求書発行区分
const SEIKYU_PRT_KBN_MAP: Record<string, string> = { '0': 'しない', '1': 'する', '2': '控のみ' };
// 請求書書式
const SEIKYU_FORM_KBN_MAP: Record<string, string> = {
  '1': '請求先単位', '2': '請求先＋得意先', '3': '請求先＋得意先＋納入先',
};
// 変更可否区分 (請求先変更 / 支払先変更)
const HENKO_KBN_MAP: Record<string, string> = { '0': '変更不可', '1': '変更可' };
// 請求明細伝票No出力区分
const SEIKYU_MEI_NO_KBN_MAP: Record<string, string> = {
  '1': '常に売上', '2': '常に受注', '3': '常に相手先番号', '4': '納品書印刷フラグ',
};
// 工事請求書パターン (MS_SYS_MEISHO KBN=91)
const SEIKYU_PRINT_FORMAT_MAP: Record<string, string> = {
  '1': '様式1 (着手金・出来高)', '2': '様式2 (鏡)', '3': '様式3 (品名明細)', '4': '様式4 (品名明細/住所)',
  '5': '様式5 (品名明細/横)', '6': '様式6 (請求書兼領収書)', '7': '様式7 (案件集約請求書)',
};
// 工事請求締め区分
const SHIME_SEIKYU_FLG_MAP: Record<string, string> = { '0': '都度', '1': '締め' };
// 消費税印刷区分
const SEIKYU_TAX_PRINT_MAP: Record<string, string> = { '1': '無し', '2': '有り' };
// 宛名印刷区分
const SEIKYU_ATENA_PRINT_MAP: Record<string, string> = { '1': '取引先', '2': '物件名' };
// 納品書種類
const NOHINSHO_SHU_KBN_MAP: Record<string, string> = { '9': '納品書 (A4)' };
// 与信限度管理区分
const YOSHIN_KANRI_KBN_MAP: Record<string, string> = { '0': '管理しない', '1': '管理する' };
// 与信限度管理処理区分
const YOSHIN_ERR_KBN_MAP: Record<string, string> = { '1': 'エラー', '2': '警告', '3': 'チェックなし' };
// 発注書単価金額印刷区分
const HACHU_TANKA_PRT_KBN_MAP: Record<string, string> = { '0': '印刷しない', '1': '印刷する' };
// 発注集計グループ
const HACHU_SUM_GRP_MAP: Record<string, string> = { '0': 'グループなし' };
// 買掛区分
const KAIKAKE_KBN_MAP: Record<string, string> = { '0': '出来高買掛', '1': '管理・労務', '2': '経費・申請' };
// 主要区分
const SHUYO_KBN_MAP: Record<string, string> = { '1': '主要先', '2': '一般先' };
// 工事判定区分 (ターコイズ画面のラジオ並び順に対応)
const KOJI_HANTEI_KBN_MAP: Record<string, string> = { '1': 'クライアント', '2': '業者', '3': 'その他', '4': '不要' };
// 手数料加算請求フラグ
const TESURYO_KASAN_FLG_MAP: Record<string, string> = { '0': '無し', '1': '有り' };

interface ShimebiRow {
  shime_gyo_no: number | null;
  seikyu_shimebi?: number | null;
  nyukin_month?: number | null;
  nyukin_day?: number | null;
  shiharai_shimebi?: number | null;
  shiharai_month?: number | null;
  shiharai_day?: number | null;
  default_shime_flg: number | null;
}

// オンプレ CGI (turquoise_search_dao.py Process=Detail) が返す MS_TORIHIKISAKI 全カラム + 名称付加項目
export interface TurquoiseDetail {
  torihikisaki_cd: number | string;
  torihikisaki_name: string | null;
  torihikisaki_rname: string | null;
  torihikisaki_kname: string | null;
  keisho_name: string | null;
  hojin_kbn: number | string | null;
  post_no: string | null;
  address1: string | null;
  address2: string | null;
  tel_no: string | null;
  keitai_tel_no: string | null;
  aite_tanto_name: string | null;
  fax_no: string | null;
  e_mail: string | null;
  torihikisaki_kbn: number | string | null;
  shokuchi_flg: number | null;
  gassan1_cd: number | null;
  gassan1_name: string | null;
  gassan2_cd: number | null;
  gassan2_name: string | null;
  chiku1_cd: number | null;
  chiku1_name: string | null;
  chiku2_cd: number | null;
  chiku2_name: string | null;
  shuyo_kbn: number | null;
  gyosya_cd: number | null;
  client_cd: number | null;
  koji_hantei_kbn: number | null;
  start_date: string | null;
  end_date: string | null;
  make_date: string | null;
  upd_date: string | null;
  sakujo_flg: number | string | null;
  // 請求情報
  uriage_bumon_cd: number | null;
  uriage_bumon_name: string | null;
  uriage_tanto_cd: number | null;
  uriage_tanto_name: string | null;
  tokuisaki_bunrui1_cd: number | null;
  tokuisaki_bunrui1_name: string | null;
  tokuisaki_bunrui2_cd: number | null;
  tokuisaki_bunrui2_name: string | null;
  tokuisaki_bunrui3_cd: number | null;
  tokuisaki_bunrui3_name: string | null;
  seikyusaki_cd: number | null;
  seikyusaki_name: string | null;
  nyukinsaki_cd: number | null;
  nyukinsaki_name: string | null;
  kaishusaki_cd: number | null;
  kaishusaki_name: string | null;
  seikyu_shimebi_list?: ShimebiRow[];
  nyukin1_kbn: number | null;
  nyukin2_kbn: number | null;
  nyukin_kbn_kingaku: number | null;
  shukin_hoho_kbn: number | null;
  seikyu_prt_kbn: number | null;
  seikyu_form_kbn: number | null;
  seikyusaki_henko_kbn: number | null;
  seikyu_print_format_kbn: number | null;
  shime_seikyu_flg: number | null;
  seikyu_tax_print: number | null;
  seikyu_atena_print: number | null;
  seikyu_mei_no_kbn: number | null;
  kyokaihi_pattern_cd_seikyu: number | null;
  kyokaihi_pattern_name_seikyu: string | null;
  nohinsho_shu_kbn: number | null;
  uriage_old_zei_kbn: number | null;
  uriage_new_zei_kbn: number | null;
  uriage_new_zei_start_date: string | null;
  uriage_zei_keisan_kbn: number | null;
  uriage_zei_hasu_kbn: number | null;
  uriage_kingaku_hasu_kbn: number | null;
  yoshin_kanri_kbn: number | null;
  yoshin_kingaku: number | null;
  yoshin_kanri_err_kbn: number | null;
  uriage_tanka_kanri_cd: number | null;
  uriage_tanka_kanri_name: string | null;
  uriage_camtanka_kanri_cd: number | null;
  uriage_camtanka_kanri_name: string | null;
  haisogyosha_cd: number | null;
  haisogyosha_name: string | null;
  azukari_soko_flg: number | null;
  itaku_soko_flg: number | null;
  tesuryo_kasan_flg: number | null;
  // 支払情報
  shiire_bumon_cd: number | null;
  shiire_bumon_name: string | null;
  shiire_tanto_cd: number | null;
  shiire_tanto_name: string | null;
  shiiresaki_bunrui1_cd: number | null;
  shiiresaki_bunrui1_name: string | null;
  shiiresaki_bunrui2_cd: number | null;
  shiiresaki_bunrui2_name: string | null;
  shiiresaki_bunrui3_cd: number | null;
  shiiresaki_bunrui3_name: string | null;
  shiharaisaki_cd: number | null;
  shiharaisaki_name: string | null;
  daihyo_shiharaisaki_cd: number | null;
  daihyo_shiharaisaki_name: string | null;
  shiharai_shimebi_list?: ShimebiRow[];
  shiharai1_kbn: number | null;
  shiharai2_kbn: number | null;
  shiharai_kbn_kingaku: number | null;
  shiharai_hoho_kbn: number | null;
  shiharaisaki_henko_kbn: number | null;
  kyokaihi_pattern_cd_shiharai: number | null;
  kyokaihi_pattern_name_shiharai: string | null;
  shiire_old_zei_kbn: number | null;
  shiire_new_zei_kbn: number | null;
  shiire_new_zei_start_date: string | null;
  shiire_zei_keisan_kbn: number | null;
  shiire_zei_hasu_kbn: number | null;
  shiire_kingaku_hasu_kbn: number | null;
  shiire_tanka_kanri_cd: number | null;
  shiire_tanka_kanri_name: string | null;
  shiire_camtanka_kanri_cd: number | null;
  shiire_camtanka_kanri_name: string | null;
  hachu_sum_grp: number | null;
  hachu_juni: number | null;
  hachu_tanka_prt_kbn: number | null;
  azuke_soko_flg: number | null;
  jutaku_soko_flg: number | null;
  kaikake_kbn: number | null;
  // 振込先口座 (MS_FURIKOMI_KOZA + MS_BANK)。未登録の取引先では全て null
  furikomi_bank_cd: number | null;
  furikomi_bank_name: string | null;
  furikomi_shiten_cd: number | null;
  furikomi_shiten_name: string | null;
  furikomi_yokinshu: number | string | null;
  furikomi_koza_bango: string | null;
  koza_meiginin_mei: string | null;
  tesuryo_futan_kbn: number | null;
  furikomi_sakujo_flg: number | null;
}

interface TurquoiseTorihikisakiDialogProps {
  open: boolean;
  onClose: () => void;
  torihikisakiCd: string | number | null;
  // 指定時はダイアログを横長にし、取引先詳細 (左) の右側へ並べて表示する
  // (顧客詳細から添付書類と横並びで照合する用途)
  sidePanel?: React.ReactNode;
  // タイトル行の右側 (閉じるボタンの左) に置く操作
  headerAction?: React.ReactNode;
}

// ---- 表示整形ヘルパー ----
const isBlank = (v: any) => v === null || v === undefined || v === '';

/** 区分値 → 名称 (未定義コードは数値のまま表示) */
const kbn = (map: Record<string, string>, v: any): string | null => {
  if (isBlank(v)) return null;
  const key = String(v);
  return map[key] ?? key;
};

/** コード + 名称 (取引先系は 8 桁ゼロ埋め)。コード 0 かつ名称なしは未設定扱い */
const codeName = (cd: any, name: any, pad = 0): React.ReactNode => {
  if (isBlank(cd) && isBlank(name)) return null;
  const cdNum = Number(cd);
  if ((isBlank(cd) || cdNum === 0) && isBlank(name)) return null;
  const cdText = isBlank(cd) ? '' : pad ? String(cd).padStart(pad, '0') : String(cd);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
      {cdText && (
        <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.75rem' }}>
          {cdText}
        </Box>
      )}
      {!isBlank(name) && <span>{name}</span>}
    </Box>
  );
};

const yen = (v: any): string | null => (isBlank(v) ? null : `${Number(v).toLocaleString('ja-JP')} 円`);

const flag = (v: any): string | null => (isBlank(v) ? null : Number(v) === 1 ? '有り' : '無し');

/** 1900-01-01 (未設定) を空扱いにする */
const dateOrNull = (v: string | null | undefined): string | null => (!v || v === EPOCH_DATE ? null : v);

/** 締日行 → 「31日締め ／ 1ヶ月後の31日」形式 (代表行は ★) */
const formatShimebi = (rows: ShimebiRow[] | undefined, kind: 'seikyu' | 'shiharai'): string | null => {
  if (!rows || rows.length === 0) return null;
  return rows
    .map((r) => {
      const shimebi = kind === 'seikyu' ? r.seikyu_shimebi : r.shiharai_shimebi;
      const month = kind === 'seikyu' ? r.nyukin_month : r.shiharai_month;
      const day = kind === 'seikyu' ? r.nyukin_day : r.shiharai_day;
      const star = Number(r.default_shime_flg) === 1 ? '★ ' : '';
      const shimeText = isBlank(shimebi) ? '-' : `${shimebi}日締め`;
      const payText = isBlank(month) && isBlank(day) ? '' : ` ／ ${month ?? 0}ヶ月後の${day ?? '-'}日`;
      return `${star}${shimeText}${payText}`;
    })
    .join('\n');
};

const Row: React.FC<{ label: string; value: any; size?: number }> = ({ label, value, size = 6 }) => (
  <Grid size={size}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
      {label}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      sx={{ mt: 0.25, color: !isBlank(value) ? 'text.primary' : 'text.disabled', wordBreak: 'break-all', whiteSpace: 'pre-line' }}
    >
      {isBlank(value) ? '-' : value}
    </Typography>
  </Grid>
);

/** タブ内の小見出し (ターコイズ画面のグループに対応) */
const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <Grid size={12}>
    <Divider textAlign="left" sx={{ '&::before, &::after': { borderColor: 'divider' } }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[400], fontSize: '0.68rem' }}>
        {label}
      </Typography>
    </Divider>
  </Grid>
);

const TAB_PANEL_SX = { bgcolor: 'grey.50', borderRadius: 2, p: 2 } as const;

const TurquoiseTorihikisakiDialog: React.FC<TurquoiseTorihikisakiDialogProps> = ({
  open,
  onClose,
  torihikisakiCd,
  sidePanel,
  headerAction,
}) => {
  const hasSide = !!sidePanel;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<TurquoiseDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'billing' | 'payment'>('basic');

  const fetchDetail = useCallback(async () => {
    if (torihikisakiCd === null || torihikisakiCd === undefined || torihikisakiCd === '') {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    setDetail(null);
    try {
      const cdStr = String(torihikisakiCd);
      const res = await axios.get(DETAIL_ENDPOINT, {
        params: { torihikisaki_cd: cdStr },
        timeout: 30000,
      });

      if (res.data?.status === 'success' && res.data.detail) {
        setDetail(res.data.detail as TurquoiseDetail);
      } else {
        setError(res.data?.message || `ターコイズ取引先コード ${cdStr} が見つかりませんでした`);
      }
    } catch (err: any) {
      console.error('TurquoiseTorihikisakiDialog fetch error', err);
      if (err.response?.status === 404) {
        setError(err.response?.data?.message || `ターコイズ取引先コード ${torihikisakiCd} が見つかりませんでした`);
      } else {
        setError(err.response?.data?.message || err.message || 'ターコイズ取引先の取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  }, [torihikisakiCd]);

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      fetchDetail();
    }
  }, [open, fetchDetail]);

  const torihikisakiKbn = detail?.torihikisaki_kbn != null ? String(detail.torihikisaki_kbn) : null;
  const hojinKbn = detail?.hojin_kbn != null ? String(detail.hojin_kbn) : null;
  const kbnInfo = torihikisakiKbn ? TORIHIKISAKI_KBN_MAP[torihikisakiKbn] : null;
  const isDeleted = detail?.sakujo_flg != null && String(detail.sakujo_flg) === '1';
  const isShokuchi = detail?.shokuchi_flg != null && Number(detail.shokuchi_flg) === 1;
  const fullAddress = detail
    ? [detail.address1, detail.address2].filter(Boolean).join('\n').trim() || null
    : null;
  const endDateDisplay =
    detail?.end_date && detail.end_date !== FAR_FUTURE_END_DATE ? detail.end_date : null;
  // 振込先は未登録の取引先が多いため、口座番号か名義があるときだけセクションごと表示する
  const hasFurikomi = !!detail && (!isBlank(detail.furikomi_koza_bango) || !isBlank(detail.koza_meiginin_mei));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // 横並び表示中は画面幅いっぱいに広げ、左を取引先詳細 (従来幅)、右をパネルに割り当てる
      maxWidth={hasSide ? false : 'sm'}
      fullWidth
      PaperProps={{ sx: hasSide ? { borderRadius: 3, width: '96vw', height: '92vh' } : { borderRadius: 3 } }}
      // ダイアログ展開時に body へ padding-right が付与され、背後のメイン画面右側に
      // 余白が生じる（スクロールバー分のレイアウトシフト）のを防ぐ
      disableScrollLock
    >
      <DialogTitle sx={{ pr: 6, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: blueGrey[500] }}>
          <Icons.AccountTree sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            ターコイズ取引先 詳細
          </Typography>
          {headerAction && <Box sx={{ ml: 'auto', mr: 1 }}>{headerAction}</Box>}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, color: 'text.disabled' }} size="small">
          <Icons.Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={hasSide ? { p: 0, display: 'flex', overflow: 'hidden' } : undefined}
      >
        {/* 取引先詳細 (横並び時は従来のダイアログ幅に固定し、この枠内だけスクロール) */}
        <Box sx={hasSide ? { width: 600, flexShrink: 0, overflowY: 'auto', px: 3, py: 2, boxSizing: 'border-box' } : undefined}>
        {loading && <LoadingView message="ターコイズ取引先を取得中" height="200px" size={40} />}
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {detail && !loading && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {String(detail.torihikisaki_cd ?? '').padStart(8, '0')}
              </Typography>
              {isDeleted && (
                <Chip
                  label="削除済み"
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              )}
              {endDateDisplay && !isDeleted && (
                <Chip
                  label="使用停止"
                  size="small"
                  color="warning"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              )}
              {hojinKbn && HOJIN_KBN_MAP[hojinKbn] && (
                <Chip
                  label={HOJIN_KBN_MAP[hojinKbn]}
                  size="small"
                  color={hojinKbn === '2' ? 'success' : 'info'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {kbnInfo && (
                <Chip
                  label={kbnInfo.label}
                  size="small"
                  color={kbnInfo.color}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {isShokuchi && (
                <Chip label="諸口" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25 }}>
              {detail.torihikisaki_name || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {detail.torihikisaki_kname || ''}
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="standard"
                sx={{
                  minHeight: 32,
                  '& .MuiTab-root': {
                    minHeight: 32,
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: 0,
                  },
                }}
              >
                <Tab value="basic" label="基本情報" />
                <Tab value="billing" label="請求情報" />
                <Tab value="payment" label="支払情報" />
              </Tabs>
            </Box>

            {activeTab === 'basic' && (
              <Grid container spacing={1.5} sx={TAB_PANEL_SX}>
                <Row label="取引先略称" value={detail.torihikisaki_rname} size={8} />
                <Row label="敬称" value={detail.keisho_name} size={4} />
                <Row label="郵便番号" value={detail.post_no} size={3} />
                <Row label="住所" value={fullAddress} size={9} />
                <Row label="電話番号" value={detail.tel_no} size={6} />
                <Row label="FAX番号" value={detail.fax_no} size={6} />
                <Row label="携帯電話" value={detail.keitai_tel_no} size={6} />
                <Row label="メールアドレス" value={detail.e_mail} size={6} />
                <Row label="取引先担当" value={detail.aite_tanto_name} size={6} />
                <Row label="取引先区分" value={kbnInfo?.label} size={6} />

                <SectionLabel label="集計・分類" />
                <Row label="実績集計先1" value={codeName(detail.gassan1_cd, detail.gassan1_name, 8)} size={6} />
                <Row label="実績集計先2" value={codeName(detail.gassan2_cd, detail.gassan2_name, 8)} size={6} />
                <Row label="地区大" value={codeName(detail.chiku1_cd, detail.chiku1_name)} size={6} />
                <Row label="地区中" value={codeName(detail.chiku2_cd, detail.chiku2_name)} size={6} />
                <Row label="主要区分" value={kbn(SHUYO_KBN_MAP, detail.shuyo_kbn)} size={4} />
                <Row label="法人区分" value={kbn(HOJIN_KBN_MAP, detail.hojin_kbn)} size={4} />
                <Row label="工事判定区分" value={kbn(KOJI_HANTEI_KBN_MAP, detail.koji_hantei_kbn)} size={4} />
                <Row label="業者コード" value={codeName(detail.gyosya_cd, null)} size={6} />
                <Row label="クライアントコード" value={codeName(detail.client_cd, null)} size={6} />

                <SectionLabel label="期間・履歴" />
                <Row label="取引開始日" value={detail.start_date} size={6} />
                <Row label="取引停止日" value={endDateDisplay} size={6} />
                <Row label="登録日時" value={detail.make_date} size={6} />
                <Row label="更新日時" value={detail.upd_date} size={6} />
              </Grid>
            )}

            {activeTab === 'billing' && (
              <Grid container spacing={1.5} sx={TAB_PANEL_SX}>
                <Row label="管理部門" value={codeName(detail.uriage_bumon_cd, detail.uriage_bumon_name)} size={6} />
                <Row label="営業担当" value={codeName(detail.uriage_tanto_cd, detail.uriage_tanto_name)} size={6} />
                <Row label="大分類" value={codeName(detail.tokuisaki_bunrui1_cd, detail.tokuisaki_bunrui1_name)} size={4} />
                <Row label="中分類" value={codeName(detail.tokuisaki_bunrui2_cd, detail.tokuisaki_bunrui2_name)} size={4} />
                <Row label="小分類" value={codeName(detail.tokuisaki_bunrui3_cd, detail.tokuisaki_bunrui3_name)} size={4} />
                <Row label="請求先" value={codeName(detail.seikyusaki_cd, detail.seikyusaki_name, 8)} size={6} />
                <Row label="入金先" value={codeName(detail.nyukinsaki_cd, detail.nyukinsaki_name, 8)} size={6} />
                <Row label="回収先" value={codeName(detail.kaishusaki_cd, detail.kaishusaki_name, 8)} size={12} />

                <SectionLabel label="締日・入金" />
                <Row label="締日G ／ 入金予定日" value={formatShimebi(detail.seikyu_shimebi_list, 'seikyu')} size={12} />
                <Row label="入金区分1" value={kbn(NYUKIN_KBN_MAP, detail.nyukin1_kbn)} size={4} />
                <Row label="入金区分2 切替額 (以上)" value={yen(detail.nyukin_kbn_kingaku)} size={4} />
                <Row label="入金区分2" value={kbn(NYUKIN_KBN_MAP, detail.nyukin2_kbn)} size={4} />
                <Row label="集金方法" value={kbn(HOHO_KBN_MAP, detail.shukin_hoho_kbn)} size={4} />
                <Row label="与信限度管理" value={kbn(YOSHIN_KANRI_KBN_MAP, detail.yoshin_kanri_kbn)} size={4} />
                <Row label="与信限度額" value={yen(detail.yoshin_kingaku)} size={4} />
                <Row label="与信限度チェック" value={kbn(YOSHIN_ERR_KBN_MAP, detail.yoshin_kanri_err_kbn)} size={4} />

                <SectionLabel label="請求書" />
                <Row label="請求書発行" value={kbn(SEIKYU_PRT_KBN_MAP, detail.seikyu_prt_kbn)} size={4} />
                <Row label="請求書書式" value={kbn(SEIKYU_FORM_KBN_MAP, detail.seikyu_form_kbn)} size={4} />
                <Row label="請求先変更" value={kbn(HENKO_KBN_MAP, detail.seikyusaki_henko_kbn)} size={4} />
                <Row label="工事請求書パターン" value={kbn(SEIKYU_PRINT_FORMAT_MAP, detail.seikyu_print_format_kbn)} size={6} />
                <Row label="工事請求締め区分" value={kbn(SHIME_SEIKYU_FLG_MAP, detail.shime_seikyu_flg)} size={6} />
                <Row label="印刷条件 消費税" value={kbn(SEIKYU_TAX_PRINT_MAP, detail.seikyu_tax_print)} size={4} />
                <Row label="印刷条件 宛名" value={kbn(SEIKYU_ATENA_PRINT_MAP, detail.seikyu_atena_print)} size={4} />
                <Row label="伝票No出力区分" value={kbn(SEIKYU_MEI_NO_KBN_MAP, detail.seikyu_mei_no_kbn)} size={4} />
                <Row label="納品伝票種類" value={kbn(NOHINSHO_SHU_KBN_MAP, detail.nohinsho_shu_kbn)} size={6} />
                <Row
                  label="協力会費パターン"
                  value={codeName(detail.kyokaihi_pattern_cd_seikyu, detail.kyokaihi_pattern_name_seikyu)}
                  size={6}
                />
                <Row label="手数料加算請求" value={kbn(TESURYO_KASAN_FLG_MAP, detail.tesuryo_kasan_flg)} size={6} />

                <SectionLabel label="税・端数" />
                <Row label="税区分 (旧)" value={kbn(ZEI_KBN_MAP, detail.uriage_old_zei_kbn)} size={4} />
                <Row label="税区分 (新)" value={kbn(ZEI_KBN_MAP, detail.uriage_new_zei_kbn)} size={4} />
                <Row label="税区分 (新) 適用日" value={dateOrNull(detail.uriage_new_zei_start_date)} size={4} />
                <Row label="税計算区分" value={kbn(ZEI_KEISAN_KBN_MAP, detail.uriage_zei_keisan_kbn)} size={4} />
                <Row label="税端数区分" value={kbn(HASU_KBN_MAP, detail.uriage_zei_hasu_kbn)} size={4} />
                <Row label="金額端数区分" value={kbn(HASU_KBN_MAP, detail.uriage_kingaku_hasu_kbn)} size={4} />

                <SectionLabel label="単価・配送・倉庫" />
                <Row label="単価管理先" value={codeName(detail.uriage_tanka_kanri_cd, detail.uriage_tanka_kanri_name, 8)} size={6} />
                <Row
                  label="期間特売単価先"
                  value={codeName(detail.uriage_camtanka_kanri_cd, detail.uriage_camtanka_kanri_name, 8)}
                  size={6}
                />
                <Row label="配送業者" value={codeName(detail.haisogyosha_cd, detail.haisogyosha_name, 8)} size={6} />
                <Row label="預かり倉庫" value={flag(detail.azukari_soko_flg)} size={3} />
                <Row label="委託倉庫" value={flag(detail.itaku_soko_flg)} size={3} />
              </Grid>
            )}

            {activeTab === 'payment' && (
              <Grid container spacing={1.5} sx={TAB_PANEL_SX}>
                <Row label="管理部門" value={codeName(detail.shiire_bumon_cd, detail.shiire_bumon_name)} size={6} />
                <Row label="仕入担当" value={codeName(detail.shiire_tanto_cd, detail.shiire_tanto_name)} size={6} />
                <Row label="大分類" value={codeName(detail.shiiresaki_bunrui1_cd, detail.shiiresaki_bunrui1_name)} size={4} />
                <Row label="中分類" value={codeName(detail.shiiresaki_bunrui2_cd, detail.shiiresaki_bunrui2_name)} size={4} />
                <Row label="小分類" value={codeName(detail.shiiresaki_bunrui3_cd, detail.shiiresaki_bunrui3_name)} size={4} />
                <Row label="支払先" value={codeName(detail.shiharaisaki_cd, detail.shiharaisaki_name, 8)} size={6} />
                <Row
                  label="代表支払先"
                  value={codeName(detail.daihyo_shiharaisaki_cd, detail.daihyo_shiharaisaki_name, 8)}
                  size={6}
                />
                {/* 協力会費は業者管理で参照頻度が高いため、振込先より上 (スクロールせず見える位置) に置く */}
                <Row
                  label="協力会費パターン"
                  value={codeName(detail.kyokaihi_pattern_cd_shiharai, detail.kyokaihi_pattern_name_shiharai)}
                  size={12}
                />

                {/* 振込先は登録のある取引先のみ表示する (ターコイズ全体で約 1/3) */}
                {hasFurikomi && (
                  <>
                    <SectionLabel label="振込先" />
                    <Row label="金融機関" value={codeName(detail.furikomi_bank_cd, detail.furikomi_bank_name, 4)} size={6} />
                    <Row label="支店" value={codeName(detail.furikomi_shiten_cd, detail.furikomi_shiten_name, 3)} size={6} />
                    <Row label="預金種別" value={kbn(YOKINSHU_MAP, detail.furikomi_yokinshu)} size={3} />
                    <Row label="口座番号" value={detail.furikomi_koza_bango} size={3} />
                    <Row label="口座名義" value={detail.koza_meiginin_mei} size={6} />
                    <Row label="振込手数料負担" value={kbn(TESURYO_FUTAN_KBN_MAP, detail.tesuryo_futan_kbn)} size={6} />
                  </>
                )}

                <SectionLabel label="締日・支払" />
                <Row label="締日G ／ 支払予定日" value={formatShimebi(detail.shiharai_shimebi_list, 'shiharai')} size={12} />
                <Row label="支払区分1" value={kbn(NYUKIN_KBN_MAP, detail.shiharai1_kbn)} size={4} />
                <Row label="支払区分2 切替額 (以上)" value={yen(detail.shiharai_kbn_kingaku)} size={4} />
                <Row label="支払区分2" value={kbn(NYUKIN_KBN_MAP, detail.shiharai2_kbn)} size={4} />
                <Row label="支払方法" value={kbn(HOHO_KBN_MAP, detail.shiharai_hoho_kbn)} size={4} />
                <Row label="支払先変更" value={kbn(HENKO_KBN_MAP, detail.shiharaisaki_henko_kbn)} size={4} />
                <Row label="買掛区分" value={kbn(KAIKAKE_KBN_MAP, detail.kaikake_kbn)} size={4} />

                <SectionLabel label="税・端数" />
                <Row label="税区分 (旧)" value={kbn(ZEI_KBN_MAP, detail.shiire_old_zei_kbn)} size={4} />
                <Row label="税区分 (新)" value={kbn(ZEI_KBN_MAP, detail.shiire_new_zei_kbn)} size={4} />
                <Row label="税区分 (新) 適用日" value={dateOrNull(detail.shiire_new_zei_start_date)} size={4} />
                <Row label="税計算区分" value={kbn(ZEI_KEISAN_KBN_MAP, detail.shiire_zei_keisan_kbn)} size={4} />
                <Row label="税端数区分" value={kbn(HASU_KBN_MAP, detail.shiire_zei_hasu_kbn)} size={4} />
                <Row label="金額端数区分" value={kbn(HASU_KBN_MAP, detail.shiire_kingaku_hasu_kbn)} size={4} />

                <SectionLabel label="単価・発注・倉庫" />
                <Row label="単価管理先" value={codeName(detail.shiire_tanka_kanri_cd, detail.shiire_tanka_kanri_name, 8)} size={6} />
                <Row
                  label="期間特売単価先"
                  value={codeName(detail.shiire_camtanka_kanri_cd, detail.shiire_camtanka_kanri_name, 8)}
                  size={6}
                />
                <Row label="発注集計グループ" value={kbn(HACHU_SUM_GRP_MAP, detail.hachu_sum_grp)} size={4} />
                <Row label="発注順位" value={isBlank(detail.hachu_juni) ? null : String(detail.hachu_juni)} size={4} />
                <Row label="発注書単価金額" value={kbn(HACHU_TANKA_PRT_KBN_MAP, detail.hachu_tanka_prt_kbn)} size={4} />
                <Row label="預け倉庫" value={flag(detail.azuke_soko_flg)} size={3} />
                <Row label="受託倉庫" value={flag(detail.jutaku_soko_flg)} size={3} />
              </Grid>
            )}
          </Box>
        )}
        </Box>
        {hasSide && (
          <Box sx={{ flex: 1, minWidth: 0, borderLeft: 1, borderColor: 'divider' }}>
            {sidePanel}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ py: 1 }}>
        <Button onClick={onClose} size="small" sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.75rem' }}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurquoiseTorihikisakiDialog;
